import React from 'react';
import { useSidePanel } from '../../context/SidePanelContext';
import { getIndexQuote, getAssetRS, getFoStockRS, getRRGData } from '../../api/client';
import CompoundPill from '../ui/CompoundPill';
import { resolveLabel, resolveId } from '../../data/indices';

// ── Search history helpers (shared with TopBar via localStorage key) ─────────
const _HISTORY_KEY   = 'sp_search_history';
const addToHistory   = sym => { try { const h = [sym, ...(JSON.parse(localStorage.getItem(_HISTORY_KEY) || '[]')).filter(s => s !== sym)].slice(0, 10); localStorage.setItem(_HISTORY_KEY, JSON.stringify(h)); } catch {} };

// ─────────────────────────────────────────────────────────────────────────────
// Module-level RS cache — survives tab switches, cleared on page reload.
// TTL: 5 minutes. Prevents 3 heavy API calls (~100 KB each) on every panel open.
// getIndexQuote() is intentionally NOT cached — it carries live LTP + change data.
// ─────────────────────────────────────────────────────────────────────────────
const _SP_CACHE     = {};
const _SP_CACHE_TTL = 5 * 60 * 1000;   // 5 minutes

async function _cachedRS(key, fetchFn) {
  const now   = Date.now();
  const entry = _SP_CACHE[key];
  if (entry && (now - entry.ts) < _SP_CACHE_TTL) return entry.data;
  const data = await fetchFn();
  if (data != null) _SP_CACHE[key] = { data, ts: now };
  return data;
}

/** Forcibly expire all cached RS data (called after a manual Sync / RS refresh). */
export function invalidateSidePanelRSCache() {
  Object.keys(_SP_CACHE).forEach(k => delete _SP_CACHE[k]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Static maps
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Map stock sector name (from SECTOR_MAP in backend) → RRG sector id.
 * Every sector name that appears in fo_stocks.py SECTOR_MAP must be here.
 */
const STOCK_SECTOR_TO_RRG = {
  // Core sectors — dedicated NSE indices
  'IT':                    'niftyit',
  'Banking':               'banknifty',
  'Financial Services':    'niftyfinsvc',
  'Pharmaceuticals':       'niftypharma',
  'Pharma':                'niftypharma',
  'Healthcare':            'niftyhealth',
  'FMCG':                  'niftyfmcg',
  'Automobile':            'niftyauto',
  'Auto':                  'niftyauto',
  'Metal':                 'niftymetal',
  'Metals':                'niftymetal',
  'Realty':                'niftyrealty',
  'Real Estate':           'niftyrealty',
  'Energy':                'niftyenergy',
  'Oil & Gas':             'niftyoilgas',
  'Infrastructure':        'niftyinfra',
  'Media':                 'niftymedia',
  'Media & Entertainment': 'niftymedia',
  'PSU Bank':              'niftypsubank',
  'Pvt Bank':              'niftypvtbank',
  'Consumer Durables':     'niftyconsumer',
  // Synthetic sector indices (dollar-volume-weighted composites)
  'Aviation':              'synth_aviation',
  'Cement':                'synth_cement',
  'Chemicals':             'synth_chemicals',
  'Conglomerates':         'synth_conglomerates',
  'Ports & Logistics':     'synth_ports_logistics',
  'Retail':                'synth_retail',
  'Technology':            'synth_technology',
  'Telecom':               'synth_telecom',
};

/**
/** Find an asset in the getAssetRS() response (multi-strategy match).
 *  Uses resolveId() from the central registry for reliable id resolution. */
function findAssetMatch(assets, name) {
  if (!name || !assets?.length) return null;
  const nLower    = name.toLowerCase();
  const nStripped = nLower.replace(/\s+/g, '');

  let found = assets.find(a => a.id.toLowerCase() === nLower);
  if (found) return found;
  found = assets.find(a => a.id.replace(/\s+/g, '').toLowerCase() === nStripped);
  if (found) return found;
  found = assets.find(a => a.label.toLowerCase().replace(/\s+/g, '') === nStripped);
  if (found) return found;
  // Registry-based id resolution: handles all ticker/kite/rrg variants
  const aid = resolveId(name);
  if (aid) return assets.find(a => a.id === aid) ?? null;
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Colour helpers
// ─────────────────────────────────────────────────────────────────────────────

// hc: signed-distance cell bg+text — vivid focus register (SidePanel surface)
function hc(v) {
  if (v >= 2.0)  return { bg:'var(--up-focus)',    color:'#fff'                  };
  if (v >= 1.0)  return { bg:'var(--up-focus-bg)', color:'var(--up-focus)'      };
  if (v >= 0.3)  return { bg:'var(--up-focus-bg)', color:'var(--up-focus)'      };
  if (v >= 0.05) return { bg:'var(--up-focus-bg)', color:'var(--up-focus)'      };
  if (v <=-2.0)  return { bg:'var(--down-focus)',    color:'#fff'                };
  if (v <=-1.0)  return { bg:'var(--down-focus-bg)', color:'var(--down-focus)'  };
  if (v <=-0.3)  return { bg:'var(--down-focus-bg)', color:'var(--down-focus)'  };
  if (v <=-0.05) return { bg:'var(--down-focus-bg)', color:'var(--down-focus)'  };
  return { bg:'var(--surface-2)', color:'var(--ink-3)' };
}

// hcFill: canvas fill — use CSS variable hex equivalents for canvas drawRect
function hcFill(v) {
  if (v >= 2.0)  return '#2CA253';   /* --up-focus */
  if (v >= 1.0)  return '#C9F1D0';   /* --up-focus-bg */
  if (v >= 0.3)  return '#C9F1D0';
  if (v >= 0.05) return '#C9F1D0';
  if (v <=-2.0)  return '#D1433C';   /* --down-focus */
  if (v <=-1.0)  return '#FFD5CD';   /* --down-focus-bg */
  if (v <=-0.3)  return '#FFD5CD';
  if (v <=-0.05) return '#FFD5CD';
  return '#EFE9DE';                  /* --surface-2 */
}

// Signal pill colours — vivid focus register (SidePanel is a focus surface)
function sigStyle(signal) {
  if (signal === 'Accelerating')  return { bg:'var(--up-focus-bg)',   color:'var(--up-focus)'   };
  if (signal === 'Decelerating')  return { bg:'var(--down-focus-bg)', color:'var(--down-focus)' };
  return { bg:'var(--surface-2)', color:'var(--ink-3)' };
}

// ─────────────────────────────────────────────────────────────────────────────
// Format helpers
// ─────────────────────────────────────────────────────────────────────────────

const fmt  = v => v != null ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—';
const fmtP = v => v != null ? `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%` : '—';
const fmtRS = v => v != null ? `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}` : '—';

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function Skeleton({ w = '60%', h = 11, mb = 5, br = 3 }) {
  return (
    <div style={{
      height: h, borderRadius: br, marginBottom: mb,
      background: 'var(--surface-2)', width: w,
      animation: 'sp-pulse 1.6s ease-in-out infinite',
    }}/>
  );
}

function SkeletonBlock({ h = 80, mb = 12 }) {
  return (
    <div style={{
      height: h, borderRadius: 5, marginBottom: mb,
      backgroundImage: 'linear-gradient(90deg, var(--surface-2) 25%, var(--surface-3) 50%, var(--surface-2) 75%)',
      backgroundSize: '200% 100%',
      animation: 'sp-shimmer 1.8s ease-in-out infinite',
    }}/>
  );
}

function SL({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '.07em',
      textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 6,
    }}>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: '.5px', background: 'var(--hair)', margin: '14px 0' }}/>;
}

// ── Signal pill ───────────────────────────────────────────────────────────────
// aboveMA: true | false | null
// Purely based on above/below 20-day MA — no trend combination
function SignalPill({ aboveMA, size = 9 }) {
  if (aboveMA === null || aboveMA === undefined) return null;
  const signal = aboveMA ? 'Accelerating' : 'Decelerating';
  const label  = aboveMA ? 'Accelerating' : 'Decelerating';
  const { bg, color } = sigStyle(signal);
  return (
    <span style={{ fontSize: size, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: bg, color }}>
      {label}
    </span>
  );
}

// ── Period Performance — inverted waterfall SVG ───────────────────────────────
// Order: 90D → 1D (long-term left, recent right)
function PeriodWaterfall({ periods, loading }) {
  const [hovIdx, setHovIdx] = React.useState(null);

  if (loading) return <SkeletonBlock h={130} mb={0}/>;
  if (!periods?.length) return null;

  const W = 340, H = 130;
  const padT = 8, padB = 22;
  const innerH = H - padT - padB;
  const zeroY  = padT + innerH / 2;
  const maxBarH = innerH / 2 - 6;

  const vals   = periods.map(p => p.v).filter(v => v != null);
  const maxAbs = vals.length ? Math.max(...vals.map(Math.abs), 0.5) : 5;
  const slotW  = W / periods.length;

  const hp         = hovIdx != null ? periods[hovIdx] : null;
  const tipLeftPct = hovIdx != null
    ? ((hovIdx * slotW + slotW / 2) / W * 100).toFixed(1)
    : '0';

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: H, display: 'block', overflow: 'visible' }}
      >
        <line x1={0} y1={zeroY} x2={W} y2={zeroY} stroke="var(--hair-strong)" strokeWidth={0.75}/>

        {periods.map((p, i) => {
          const midX = i * slotW + slotW / 2;
          const bW   = Math.max(slotW * 0.65, 6);
          const bX   = midX - bW / 2;

          const periodLabel = (
            <text key={`lbl-${i}`} x={midX} y={H - 4} textAnchor="middle" fontSize={10}
              fill="var(--ink-3)" fontFamily="var(--font-ui)">
              {p.label}
            </text>
          );

          if (p.v == null) {
            return (
              <g key={p.label} style={{ cursor: 'default' }}
                onMouseEnter={() => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)}>
                <rect x={bX} y={padT} width={bW} height={innerH} fill="transparent"/>
                <text x={midX} y={zeroY + 4} textAnchor="middle" fontSize={10} fill="var(--ink-3)">—</text>
                {periodLabel}
              </g>
            );
          }

          const isPos  = p.v >= 0;
          const barH   = Math.max(2, (Math.abs(p.v) / maxAbs) * maxBarH);
          const fill   = hcFill(p.v);
          const barY   = isPos ? zeroY : zeroY - barH;
          const valLbl = `${p.v >= 0 ? '+' : ''}${p.v.toFixed(1)}%`;
          const labelY = isPos ? zeroY - 5 : zeroY + 10;

          return (
            <g key={p.label} style={{ cursor: 'default' }}
              onMouseEnter={() => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)}>
              <rect x={bX} y={padT} width={bW} height={innerH} fill="transparent"/>
              <rect x={bX} y={barY} width={bW} height={barH} fill={fill} rx={2}/>
              <text x={midX} y={labelY} textAnchor="middle" fontSize={10}
                fill={isPos ? '#1a5c1a' : '#881e10'}
                fontFamily="var(--font-data)">
                {valLbl}
              </text>
              {periodLabel}
            </g>
          );
        })}
      </svg>

      {hp && (
        <div style={{
          position:      'absolute',
          left:          `clamp(0px, calc(${tipLeftPct}% - 40px), calc(100% - 82px))`,
          top:           4,
          pointerEvents: 'none',
          fontSize:      9,
          fontFamily:    'var(--font-data)',
          lineHeight:    1.5,
          zIndex:        50,
          whiteSpace:    'nowrap',
          textAlign:     'center',
          color:         'rgba(80,80,80,0.85)',
        }}>
          {hp.ref_date ? (
            <>
              <div>₹{hp.ref_price?.toFixed(2)}</div>
              <div>{hp.ref_date}</div>
            </>
          ) : (
            <div>no history</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Mini price sparkline ──────────────────────────────────────────────────────
// MiniSparkline — SVG sparkline with optional synchronized crosshair readout.
// dates[] must be ISO strings aligned 1:1 with closes[].
const SP_VW = 200, SP_VH = 48, SP_PAD = 3, SP_DATE_H = 12;
// ISO "YYYY-MM-DD" → "DD-MM"
const fmtDate = d => (d?.length >= 10) ? `${d.slice(8,10)}-${d.slice(5,7)}` : '';
function MiniSparkline({ closes, dates, crosshairDate, onCrosshairChange }) {
  if (!closes?.length || closes.length < 2) return null;
  const mn  = Math.min(...closes);
  const mx  = Math.max(...closes);
  const rng = mx - mn || 1;

  const chartB = SP_VH - SP_PAD - SP_DATE_H;   // bottom of chart area — reserves room for date
  const svgX = i => SP_PAD + (i / (closes.length - 1)) * (SP_VW - SP_PAD * 2);
  const svgY = v => chartB - ((v - mn) / rng) * (chartB - SP_PAD);

  const pts   = closes.map((v, i) => `${svgX(i).toFixed(1)},${svgY(v).toFixed(1)}`).join(' ');
  const last  = closes[closes.length - 1];
  const first = closes[0];
  const isUp  = last >= first;
  const color = isUp ? '#1a6a1a' : '#c41a1a';

  // Crosshair: find hovered index by date
  const hoverIdx = (crosshairDate && dates?.length) ? dates.indexOf(crosshairDate) : -1;
  const hoverX   = hoverIdx >= 0 ? svgX(hoverIdx) : null;
  const hoverV   = hoverIdx >= 0 ? closes[hoverIdx] : null;
  const hoverY   = hoverV  != null ? svgY(hoverV) : null;
  const hoverCol = hoverV  != null ? (hoverV >= first ? '#1a6a1a' : '#c41a1a') : color;

  const fmtP = v => v >= 10000 ? v.toLocaleString('en-IN', { maximumFractionDigits:0 })
             : v >= 100  ? v.toFixed(1) : v.toFixed(2);

  const handleMouseMove = e => {
    if (!dates?.length || !onCrosshairChange) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const i    = Math.round(frac * (closes.length - 1));
    if (dates[i]) onCrosshairChange(dates[i]);
  };

  return (
    <svg viewBox={`0 0 ${SP_VW} ${SP_VH}`}
      style={{ width:'100%', height:SP_VH, display:'block', opacity:.88,
               cursor: dates?.length ? 'crosshair' : 'default' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => onCrosshairChange?.(null)}>

      <polyline points={pts} fill="none" stroke={color} strokeWidth={2}
        strokeLinejoin="round" strokeLinecap="round"/>

      {/* Rightmost dot — hidden while crosshair is active */}
      {hoverX == null && (
        <circle cx={svgX(closes.length - 1).toFixed(1)} cy={svgY(last).toFixed(1)}
          r={3.5} fill={color}/>
      )}

      {/* Crosshair overlay */}
      {hoverX != null && hoverY != null && (<>
        <line x1={hoverX.toFixed(1)} y1={SP_PAD}
              x2={hoverX.toFixed(1)} y2={chartB}
          stroke="rgba(150,150,150,0.5)" strokeWidth={0.8} strokeDasharray="2,2"/>
        <circle cx={hoverX.toFixed(1)} cy={hoverY.toFixed(1)} r={3.5} fill={hoverCol}/>
        {/* Price value — top */}
        <text
          x={(hoverX > SP_VW / 2 ? hoverX - 5 : hoverX + 5).toFixed(1)}
          y={(SP_PAD + 8).toFixed(1)}
          textAnchor={hoverX > SP_VW / 2 ? 'end' : 'start'}
          fontSize={11} fontFamily="var(--font-data)" fontWeight="700"
          fill={hoverCol} opacity={0.95}>
          {fmtP(hoverV)}
        </text>
        {/* Date — bottom zone, centred on crosshair x, clipped to edges */}
        {dates?.[hoverIdx] && (
          <text
            x={Math.max(18, Math.min(SP_VW - 18, hoverX)).toFixed(1)}
            y={(SP_VH - 2).toFixed(1)}
            textAnchor="middle"
            fontSize={9} fontFamily="var(--font-data)"
            fill="var(--ink-3)" opacity={0.75}>
            {fmtDate(dates[hoverIdx])}
          </text>
        )}
      </>)}
    </svg>
  );
}

// ── 52W High/Low range bar ────────────────────────────────────────────────────
function RangeBar52W({ ltp, high52w, low52w, loading }) {
  if (loading) return <SkeletonBlock h={36} mb={0}/>;
  if (high52w == null || low52w == null || ltp == null) return null;

  const range = high52w - low52w;
  const pct   = range > 0 ? Math.max(0, Math.min(1, (ltp - low52w) / range)) : 0.5;
  const pos   = `${(pct * 100).toFixed(1)}%`;

  return (
    <div style={{ padding: '8px 0', marginBottom: 4 }}>
      {/* Track */}
      <div style={{ position: 'relative', height: 6, borderRadius: 3, background: 'var(--surface-3)' }}>
        {/* Filled portion */}
        <div style={{
          position: 'absolute', left: 0, top: 0, height: '100%',
          width: pos, borderRadius: 3,
          background: pct > 0.7 ? '#388e3c' : pct < 0.3 ? '#e53935' : '#f59e0b',
        }}/>
        {/* Thumb */}
        <div style={{
          position: 'absolute', top: '50%', left: pos,
          transform: 'translate(-50%,-50%)',
          width: 10, height: 10, borderRadius: '50%',
          background: pct > 0.7 ? '#388e3c' : pct < 0.3 ? '#e53935' : '#f59e0b',
          border: '2px solid var(--surface)', boxShadow: '0 1px 3px rgba(0,0,0,.25)',
        }}/>
      </div>
      {/* Labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 11 }}>
        <span style={{ color: 'var(--down-focus)', fontFamily: 'var(--font-data)', fontWeight: 600 }}>
          52L ₹{fmt(low52w)}
        </span>
        <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>
          {(pct * 100).toFixed(0)}% of range
        </span>
        <span style={{ color: 'var(--up-focus)', fontFamily: 'var(--font-data)', fontWeight: 600 }}>
          52H ₹{fmt(high52w)}
        </span>
      </div>
    </div>
  );
}

// ── RS Mini Chart canvas ──────────────────────────────────────────────────────
// series: [{ v: rs/100, ma: ma/100|null, date }]
// Supports synchronized crosshair: crosshairDate (ISO string) + onCrosshairChange.
function RSMiniChart({ series, crosshairDate, onCrosshairChange }) {
  const canvasRef = React.useRef(null);
  const isDark    = document.documentElement.classList.contains('dark');

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !series?.length) return;
    const dpr = window.devicePixelRatio || 1;
    const W   = canvas.offsetWidth  || 310;
    const H   = canvas.offsetHeight || 100;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const pad = { l: 6, r: 6, t: 14, b: 14 };  // top: RS/MA label; bottom: date label

    const vals   = series.map(d => d.v);
    const maVals = series.map(d => d.ma ?? null);

    const allV = [...vals, ...maVals.filter(x => x != null)];
    if (!allV.length) return;
    const mn  = Math.min(...allV);
    const mx  = Math.max(...allV);
    const rng = mx - mn || 0.02;

    const n  = vals.length;
    const px = i => pad.l + (i / Math.max(n - 1, 1)) * (W - pad.l - pad.r);
    const py = v => pad.t + (1 - (v - mn) / rng) * (H - pad.t - pad.b);

    ctx.clearRect(0, 0, W, H);

    // Parity reference line at RS = 1.0
    const refY = py(1.0);
    if (refY >= pad.t && refY <= H - pad.b) {
      ctx.beginPath();
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
      ctx.lineWidth   = 0.6;
      ctx.moveTo(pad.l, refY); ctx.lineTo(W - pad.r, refY);
      ctx.stroke(); ctx.setLineDash([]);
    }

    // RS line — grey before MA exists, green/red after
    for (let i = 1; i < n; i++) {
      if (vals[i] == null || vals[i - 1] == null) continue;
      const maHere = maVals[i];
      const strokeColor = maHere == null
        ? (isDark ? 'rgba(200,200,200,0.4)' : 'rgba(100,100,100,0.35)')
        : vals[i] >= maHere ? '#1a6a1a' : '#c41a1a';
      ctx.beginPath();
      ctx.moveTo(px(i - 1), py(vals[i - 1]));
      ctx.lineTo(px(i),     py(vals[i]));
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth   = 1.8;
      ctx.lineJoin    = 'round';
      ctx.globalAlpha = maHere == null ? 0.6 : 0.9;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // MA line — from first non-null bar
    ctx.beginPath();
    let firstMA = true;
    for (let i = 0; i < n; i++) {
      if (maVals[i] == null) continue;
      firstMA ? ctx.moveTo(px(i), py(maVals[i])) : ctx.lineTo(px(i), py(maVals[i]));
      firstMA = false;
    }
    ctx.strokeStyle = isDark ? 'rgba(200,200,200,0.9)' : 'rgba(30,30,30,0.85)';
    ctx.lineWidth   = 1.2;
    ctx.lineJoin    = 'round';
    ctx.stroke();

    // ── Crosshair overlay ───────────────────────────────────────────────────
    if (crosshairDate) {
      const ci = series.findIndex(d => d.date === crosshairDate);
      if (ci >= 0) {
        const cx  = px(ci);
        const val = vals[ci];
        const maV = maVals[ci];

        // Vertical dashed line
        ctx.beginPath();
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)';
        ctx.lineWidth   = 0.8;
        ctx.moveTo(cx, pad.t); ctx.lineTo(cx, H - pad.b);
        ctx.stroke(); ctx.setLineDash([]);

        // Dot on RS line
        if (val != null) {
          const dotCol = maV == null
            ? (isDark ? 'rgba(200,200,200,0.8)' : 'rgba(100,100,100,0.8)')
            : val >= maV ? '#1a6a1a' : '#c41a1a';
          ctx.beginPath();
          ctx.arc(cx, py(val), 3.5, 0, Math.PI * 2);
          ctx.fillStyle = dotCol;
          ctx.fill();
        }

        // Dot on MA line
        if (maV != null) {
          ctx.beginPath();
          ctx.arc(cx, py(maV), 2.5, 0, Math.PI * 2);
          ctx.fillStyle = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(30,30,30,0.85)';
          ctx.fill();
        }

        // Top label: RS/MA values (no date)
        const relPct = val != null ? ((val - 1) * 100) : null;
        const maPct  = maV  != null ? ((maV  - 1) * 100) : null;
        const sign   = v => v >= 0 ? '+' : '';
        let valLabel = '';
        if (relPct != null) valLabel += `RS ${sign(relPct)}${relPct.toFixed(1)}%`;
        if (maPct  != null) valLabel += `  MA ${sign(maPct)}${maPct.toFixed(1)}%`;

        const textX = cx > W / 2 ? cx - 5 : cx + 5;
        const align = cx > W / 2 ? 'right' : 'left';
        ctx.font      = 'bold 9px "IBM Plex Sans", system-ui, sans-serif';
        ctx.textAlign = align;
        ctx.fillStyle = isDark ? 'rgba(220,220,220,0.9)' : 'rgba(30,30,30,0.9)';
        if (valLabel) ctx.fillText(valLabel, textX, pad.t - 3);

        // Bottom label: date only (DD-MM), centred on crosshair x, clipped to edges
        const datePart = fmtDate(crosshairDate);
        if (datePart) {
          const dateX = Math.max(20, Math.min(W - 20, cx));
          ctx.font      = '9px "IBM Plex Sans", system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillStyle = isDark ? 'rgba(200,200,200,0.7)' : 'rgba(80,80,80,0.7)';
          ctx.fillText(datePart, dateX, H - 1);
        }
      }
    }
  }, [series, isDark, crosshairDate]);

  // Mouse → date lookup, lifted to parent via onCrosshairChange
  const handleMouseMove = React.useCallback(e => {
    if (!canvasRef.current || !series?.length) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const i    = Math.round(frac * (series.length - 1));
    const date = series[i]?.date;
    if (date) onCrosshairChange?.(date);
  }, [series, onCrosshairChange]);

  return (
    <div style={{ height:100, width:'100%', cursor:'crosshair' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => onCrosshairChange?.(null)}>
      <canvas ref={canvasRef} style={{ width:'100%', height:'100%', display:'block' }}/>
    </div>
  );
}

// ── RRG Quadrant mini-canvas ──────────────────────────────────────────────────
const RRG_QUAD_COLOR = {
  leading:   '#1a6a1a', weakening: '#8a5a00',
  lagging:   '#881e10', improving: '#1a4a8a',
};
const RRG_QUAD_FILL = {
  leading:   'rgba(26,106,26,.065)',  weakening: 'rgba(138,90,0,.065)',
  lagging:   'rgba(136,30,16,.065)', improving: 'rgba(26,74,138,.065)',
};
const RRG_QUAD_META = {
  leading:   { label:'Leading',   bg:'rgba(26,106,26,.10)',  border:'rgba(26,106,26,.25)',  text:'#1a6a1a' },
  weakening: { label:'Weakening', bg:'rgba(138,90,0,.10)',   border:'rgba(138,90,0,.25)',   text:'#8a5a00' },
  lagging:   { label:'Lagging',   bg:'rgba(136,30,16,.10)',  border:'rgba(136,30,16,.25)',  text:'#881e10' },
  improving: { label:'Improving', bg:'rgba(26,74,138,.10)',  border:'rgba(26,74,138,.25)',  text:'#1a4a8a' },
};
const RRG_QUAD_DESC = {
  leading:   'Above NIFTY 50 in both RS-Ratio and RS-Momentum — outperforming with improving momentum.',
  weakening: 'Above NIFTY 50 in RS-Ratio but momentum declining — outperforming but losing steam.',
  lagging:   'Below NIFTY 50 in both RS-Ratio and RS-Momentum — underperforming with falling momentum.',
  improving: 'Below NIFTY 50 in RS-Ratio but momentum turning up — underperforming but recovering.',
};

function getQuadrant(x, y) {
  if (x >= 100 && y >= 100) return 'leading';
  if (x >= 100 && y <  100) return 'weakening';
  if (x <  100 && y <  100) return 'lagging';
  return 'improving';
}

function RRGMiniCanvas({ trail, quadrant }) {
  const canvasRef = React.useRef(null);
  const isDark    = document.documentElement.classList.contains('dark');

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !trail?.length) return;
    const dpr  = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return;
    canvas.width  = rect.width  * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    const pad = { l: 28, r: 12, t: 14, b: 26 };
    const cW = W - pad.l - pad.r;
    const cH = H - pad.t - pad.b;

    let maxDist = 4;
    trail.forEach(p => { maxDist = Math.max(maxDist, Math.abs(p.x - 100), Math.abs(p.y - 100)); });
    const RANGE = maxDist + 1.5;
    const cx = pad.l + cW / 2;
    const cy = pad.t + cH / 2;
    const toX = v => cx + (v - 100) * (cW / 2) / RANGE;
    const toY = v => cy - (v - 100) * (cH / 2) / RANGE;

    const bg = getComputedStyle(document.documentElement).getPropertyValue('--card').trim() || '#fff';
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = RRG_QUAD_FILL.leading;   ctx.fillRect(cx, pad.t, pad.l + cW - cx, cy - pad.t);
    ctx.fillStyle = RRG_QUAD_FILL.weakening; ctx.fillRect(cx, cy, pad.l + cW - cx, pad.t + cH - cy);
    ctx.fillStyle = RRG_QUAD_FILL.lagging;   ctx.fillRect(pad.l, cy, cx - pad.l, pad.t + cH - cy);
    ctx.fillStyle = RRG_QUAD_FILL.improving; ctx.fillRect(pad.l, pad.t, cx - pad.l, cy - pad.t);

    ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)';
    ctx.lineWidth = 0.5; ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(cx, pad.t);   ctx.lineTo(cx, pad.t + cH); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pad.l, cy);   ctx.lineTo(pad.l + cW, cy); ctx.stroke();
    ctx.setLineDash([]);

    ctx.font = "9px 'IBM Plex Sans', system-ui, sans-serif";
    ctx.fillStyle = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';
    ctx.textAlign = 'center'; ctx.fillText('100', cx, pad.t + cH + 14);
    ctx.textAlign = 'right';  ctx.fillText('100', pad.l - 4, cy + 3);

    ctx.font = "bold 9px 'IBM Plex Sans', system-ui, sans-serif";
    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(26,106,26,0.45)';  ctx.fillText('Leading',   pad.l + cW - 2, pad.t + 10);
    ctx.fillStyle = 'rgba(138,90,0,0.45)';   ctx.fillText('Weakening', pad.l + cW - 2, pad.t + cH - 4);
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(26,74,138,0.45)';  ctx.fillText('Improving', pad.l + 2, pad.t + 10);
    ctx.fillStyle = 'rgba(136,30,16,0.45)';  ctx.fillText('Lagging',   pad.l + 2, pad.t + cH - 4);

    const lineColor = RRG_QUAD_COLOR[quadrant] || '#666';

    if (trail.length > 1) {
      ctx.beginPath();
      ctx.moveTo(toX(trail[0].x), toY(trail[0].y));
      for (let i = 1; i < trail.length; i++) ctx.lineTo(toX(trail[i].x), toY(trail[i].y));
      ctx.strokeStyle = lineColor; ctx.lineWidth = 1.5;
      ctx.lineJoin = 'round'; ctx.globalAlpha = 0.55;
      ctx.stroke(); ctx.globalAlpha = 1;
    }

    trail.forEach((pt, i) => {
      const isLast = i === trail.length - 1;
      const alpha  = 0.15 + (i / Math.max(trail.length - 1, 1)) * 0.85;
      const r      = isLast ? 5 : 2.5;
      ctx.beginPath();
      ctx.arc(toX(pt.x), toY(pt.y), r, 0, Math.PI * 2);
      ctx.fillStyle   = lineColor;
      ctx.globalAlpha = isLast ? 1 : alpha;
      ctx.fill();
      if (isLast) {
        ctx.globalAlpha = 1;
        ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.8)' : '#fff';
        ctx.lineWidth   = 1.5;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    });
  }, [trail, quadrant, isDark]);

  return (
    <div style={{ height: 160, width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }}/>
    </div>
  );
}

// ── RRG Quadrant pill (for use alongside RRG chart only) ─────────────────────
function QuadrantPill({ quadrant }) {
  if (!quadrant) return null;
  const qc = RRG_QUAD_META[quadrant];
  if (!qc) return null;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 12,
      background: qc.bg, border: `.5px solid ${qc.border}`, color: qc.text,
    }}>
      {qc.label}
    </span>
  );
}

// ── isBenchmark — covers all NIFTY 50 / benchmark ticker variants ─────────────
const BENCHMARK_IDS = new Set(['NIFTY50', '^NSEI', 'NIFTY50INDEX', 'NIFTY 50']);
function isBenchmark(name) {
  if (!name) return false;
  const norm = name.toUpperCase().replace(/\s+/g, '');
  if (BENCHMARK_IDS.has(norm)) return true;
  if (norm === 'NIFTY50' || norm === '^NSEI') return true;
  return false;
}

// ── SectorRotationDisplay — unified sector/stock RRG block ───────────────────
function SectorRotationDisplay({ sectorData, secLoad, crosshairDate, onCrosshairChange, isStock }) {
  if (secLoad) {
    return <><SkeletonBlock h={50} mb={6}/><SkeletonBlock h={160}/></>;
  }
  if (!sectorData) return null;

  const { label, series, trail, quadrant, rsRatio, rsMom, aboveMA, rsCurrent, signal: secSignalField } = sectorData;

  const secRatio    = rsCurrent != null ? rsCurrent / 100 : null;
  const secStrength = secRatio == null ? 'Equal' : secRatio > 1.005 ? 'Strong' : secRatio < 0.995 ? 'Weak' : 'Equal';
  // Signal is canonical from backend. Null if absent — render site shows '—'.
  const secMomentum = secSignalField ?? null;

  return (
    <>
      {/* Sector name + CompoundPill signal */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
        <span style={{ fontSize:12, fontWeight:600, color:'var(--ink)' }}>{label}</span>
        <span title="Strength = 20W daily RS ratio vs NIFTY · Momentum = ±0.5% band around 20-day MA">
          {secMomentum != null
            ? <CompoundPill strength={secStrength} momentum={secMomentum}/>
            : <span style={{color:'var(--ink-3)', fontSize:11}}>—</span>}
        </span>
      </div>

      {/* RS sparkline (stocks show sector RS vs NIFTY) */}
      {series?.length >= 4 && (
        <div style={{ marginBottom:10 }}>
          <div className="card" style={{ padding:'6px 6px' }}>
            <RSMiniChart series={series} crosshairDate={crosshairDate} onCrosshairChange={onCrosshairChange}/>
            <div style={{ fontSize:10, color:'var(--ink-3)', opacity:.6, marginTop:5 }}>
              Green = above 20-day MA · Red = below · Black = MA
            </div>
          </div>
        </div>
      )}

      {/* RRG quadrant context card — sector indices only */}
      {!isStock && quadrant && (() => {
        const qc = RRG_QUAD_META[quadrant];
        return qc ? (
          <div style={{ padding:'9px 12px', borderRadius:6, marginBottom:10, background:qc.bg, border:`.5px solid ${qc.border}` }}>
            <div style={{ fontWeight:700, fontSize:13, color:qc.text, marginBottom:3 }}>{qc.label}</div>
            <div style={{ fontSize:11, color:'var(--ink-2)', lineHeight:1.5 }}>{RRG_QUAD_DESC[quadrant]}</div>
          </div>
        ) : null;
      })()}

      {/* RS-Ratio / RS-Momentum tiles — sector indices only */}
      {!isStock && rsRatio != null && rsMom != null && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:10 }}>
          {[
            { l:'RS-Ratio',    v: rsRatio, note:'> 100 = outperforming' },
            { l:'RS-Momentum', v: rsMom,   note:'> 100 = improving'     },
          ].map(({ l, v, note }) => (
            <div key={l} style={{ background:'var(--surface-2)', borderRadius:4, padding:'7px 9px' }}>
              <div style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:2 }}>{l}</div>
              <div style={{ fontFamily:'var(--font-data)', fontSize:15, fontWeight:700, color: v >= 100 ? 'var(--up-focus)':'var(--down-focus)' }}>
                {v?.toFixed(2) ?? '—'}
              </div>
              <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:2 }}>{note}</div>
            </div>
          ))}
        </div>
      )}

      {/* RRG trail canvas */}
      {trail?.length > 0 && (
        <>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
            <span style={{ fontSize:11, color:'var(--ink-3)' }}>
              {isStock ? 'Sector Rotation — Trail oldest → newest ● (weekly)' : 'Trail — oldest → newest ●'}
            </span>
            {isStock && <QuadrantPill quadrant={quadrant}/>}
          </div>
          <div className="card" style={{ padding:'6px 6px', marginBottom:10 }}>
            <RRGMiniCanvas trail={trail} quadrant={quadrant}/>
          </div>
        </>
      )}

      {!series && !trail?.length && (
        <div style={{ fontSize:11, color:'var(--ink-3)', fontStyle:'italic' }}>Sector data not available</div>
      )}

      {!isStock && (
        <div style={{ fontSize:11, color:'var(--ink-3)', paddingTop:8, borderTop:'.5px solid var(--hair)', lineHeight:1.5, marginBottom:10 }}>
          RS-Ratio and RS-Momentum centred at 100 (NIFTY 50 benchmark) · WMA · Weekly · L20
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main SidePanel
// ─────────────────────────────────────────────────────────────────────────────
export default function SidePanel() {
  const { ticker, close, open: openPanel } = useSidePanel();
  const open = !!ticker;
  const panelRef = React.useRef(null);

  // Scroll panel back to top whenever a new ticker is opened
  React.useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.scrollTop = 0;
    }
  }, [open, ticker?.name]);

  // Lock body scroll while panel is open — prevents background page scrolling on mobile
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // ── Data states ────────────────────────────────────────────────────────────
  const [priceData,     setPriceData]     = React.useState(null);
  const [priceLoad,     setPriceLoad]     = React.useState(false);
  const [priceErr,      setPriceErr]      = React.useState(null);
  const [crosshairDate, setCrosshairDate] = React.useState(null);
  const handleCrosshair = React.useCallback(d => setCrosshairDate(d), []);

  const [rsData,     setRsData]     = React.useState(null);
  const [rsLoad,     setRsLoad]     = React.useState(false);

  const [sectorData, setSectorData] = React.useState(null);
  const [secLoad,    setSecLoad]    = React.useState(false);

  const [matchInfo,  setMatchInfo]  = React.useState(null);

  const [refreshKey,    setRefreshKey]    = React.useState(0);
  const [refreshing,    setRefreshing]    = React.useState(false);
  const [lastRefreshed, setLastRefreshed] = React.useState(null);
  const [refreshErr,    setRefreshErr]    = React.useState(false);
  const isRefreshRef = React.useRef(false);
  const refreshErrTimerRef = React.useRef(null);

  // Ticker switcher — inline search in panel header
  const [tickerSearch,   setTickerSearch]   = React.useState(false);
  const [tickerQ,        setTickerQ]        = React.useState('');
  const [searchUniverse, setSearchUniverse] = React.useState([]);
  const tickerInputRef = React.useRef(null);

  React.useEffect(() => {
    const _BASE = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    fetch(`${_BASE}/api/journal/symbol-universe`)
      .then(r => r.json())
      .then(d => setSearchUniverse(d.symbols || []))
      .catch(() => {});
  }, []);

  // Close switcher on ticker change
  React.useEffect(() => { setTickerSearch(false); setTickerQ(''); }, [ticker?.name]);

  // New sections — populated from /api/sidepanel/{ticker}
  const [panelMeta,  setPanelMeta]  = React.useState(null);  // { watchlists, open_positions }

  // ── Fetch on panel open ────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!ticker?.name || !open) {
      setPriceData(null); setPriceErr(null); setPriceLoad(false);
      setRsData(null);    setRsLoad(false);
      setSectorData(null); setSecLoad(false);
      setMatchInfo(null);
      setPanelMeta(null);
      return;
    }

    const name = ticker.name;
    const BASE = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

    // On soft refresh (same ticker, manual re-fetch): keep existing data to avoid flicker.
    // On ticker change: clear everything so the panel starts blank.
    const softRefresh = isRefreshRef.current;
    isRefreshRef.current = false;
    if (!softRefresh) {
      setPriceData(null); setPriceErr(null); setPriceLoad(true); setCrosshairDate(null);
      setRsData(null);    setRsLoad(true);
      setSectorData(null); setSecLoad(true);
      setMatchInfo(null);
    }

    // ── Speculative stock fetches — fired immediately, before Phase 1 resolves ─
    // For stocks (the common case) this means price data arrives in parallel with
    // RS data, eliminating the visible "RS loads → pause → price loads" sequence.
    // If the ticker turns out to be an asset/index, these 404s are caught + discarded.
    const _stockQuoteP   = fetch(`${BASE}/api/prices/stock/${encodeURIComponent(name)}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .catch(() => null);
    const _stockHistoryP = fetch(`${BASE}/api/prices/stock/${encodeURIComponent(name)}/history?days=200`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .catch(() => null);

    // ── Phase 1: all cached lookups + index-price in parallel ─────────────────
    // RS/RRG calls are served from a 5-minute module-level cache after the first
    // panel open — subsequent opens for any stock/index are near-instant.
    // getIndexQuote is never cached (live LTP + change_pct must always be fresh).
    Promise.allSettled([
      _cachedRS('assetRS',   () => getAssetRS()),                      // [0]
      _cachedRS('foStockRS', () => getFoStockRS()),                    // [1]
      _cachedRS('rrgWeekly', () => getRRGData('weekly', 52, 'wma', 20)), // [2]
      getIndexQuote(name),                                             // [3] — always fresh
    ]).then(([assetR, stockR, rrgR, idxPriceR]) => {

      const assets  = assetR.value?.assets  ?? [];
      const stocks  = stockR.value?.stocks  ?? [];
      const sectors = rrgR.value?.sectors   ?? [];

      // ── Type inference from data, never from ticker.sector ────────────────
      const stockMatch = stocks.find(s => s.symbol === name);
      const assetMatch = stockMatch ? null : findAssetMatch(assets, name);
      const matchType  = stockMatch ? 'stock' : assetMatch ? 'asset' : null;
      const matchData  = stockMatch ?? assetMatch;

      setMatchInfo({
        type:        matchType,
        isF0:        matchType === 'stock',
        label:       matchData?.label ?? null,
        sectorLabel: matchType === 'stock'
                     ? (matchData?.sector ?? null)
                     : (matchData?.category ?? null),
      });

      // ── RS data — use backend pre-computed values directly ────────────────
      // rs_5d / rs_1m / rs_3m are absolute point deltas on 100-normalised scale.
      // Signal = purely above_ma (no trend combination).
      if (matchData?.rs_series?.length) {
        const series  = matchData.rs_series.map(p => ({
          v:    p.rs  / 100,
          ma:   p.ma  != null ? p.ma / 100 : null,
          date: p.date,
        }));
        const aboveMA = matchData.above_ma;
        const rs5d    = matchData.rs_5d ?? null;

        setRsData({
          series,
          ratio:   matchData.rs_current != null ? matchData.rs_current / 100 : null,
          rs1W:    rs5d,
          rs1M:    matchData.rs_1m ?? null,
          rs3M:    matchData.rs_3m ?? null,
          aboveMA,
          // Signal is canonical from backend. Null if absent — render site shows '—'.
          signal:  matchData.signal ?? null,
          trend:   rs5d == null ? 'Flat'
                 : rs5d  >  0.5 ? 'Rising'
                 : rs5d  < -0.5 ? 'Falling' : 'Flat',
          building: false,
        });
      } else if (matchData?.building) {
        // Thin-history instrument: series not yet available, MA window still accumulating.
        // Distinct from "no data" — we know the stock exists, just needs more history.
        setRsData({ series: [], building: true, signal: null, ratio: null,
                    rs1W: null, rs1M: null, rs3M: null, aboveMA: null, trend: 'Flat' });
      }
      setRsLoad(false);

      // ── Sector / RRG data ─────────────────────────────────────────────────
      if (matchType === 'stock' && matchData?.sector) {
        const sectorId = STOCK_SECTOR_TO_RRG[matchData.sector] ?? null;
        const secRRG   = sectorId ? sectors.find(s => s.id === sectorId) : null;
        const secAsset = sectorId ? assets.find(a => a.id === sectorId)  : null;

        if (secRRG || secAsset) {
          const latestPt = secRRG?.trail?.[secRRG.trail.length - 1];
          // Signal from backend (_infer_signal, ±0.5% band) — single source of truth.
          const secAboveMA = secAsset?.above_ma ?? null;
          const secSignal  = secAsset?.signal ?? (
            secAboveMA === true  ? 'Accelerating' :
            secAboveMA === false ? 'Decelerating' : 'Flat'
          );
          setSectorData({
            label:      secRRG?.label ?? secAsset?.label ?? matchData.sector,
            quadrant:   secRRG && latestPt ? getQuadrant(latestPt.x, latestPt.y) : null,
            trail:      secRRG?.trail ?? [],
            rsRatio:    latestPt?.x ?? null,
            rsMom:      latestPt?.y ?? null,
            aboveMA:    secAboveMA,
            signal:     secSignal,
            rsCurrent:  secAsset?.rs_current ?? null,
            series:     secAsset?.rs_series?.map(p => ({
              v:    p.rs / 100,
              ma:   p.ma != null ? p.ma / 100 : null,
              date: p.date,
            })) ?? null,
          });
        }
      } else if (matchType === 'asset') {
        // Derive quadrant from the WMA/W·20 trail fetched above (same algo as
        // the Sector Rotation tab default — consistent across all callers).
        const nameUp = name.toUpperCase().replace(/\s+/g, '');
        const secRRG = sectors.find(s =>
          s.id === assetMatch?.id ||
          s.ticker.toUpperCase() === nameUp ||
          s.id.toLowerCase() === nameUp.toLowerCase() ||
          s.label.toLowerCase().replace(/\s+/g, '') === name.toLowerCase().replace(/\s+/g, '')
        );
        if (secRRG) {
          const latestPt = secRRG.trail?.[secRRG.trail.length - 1];
          setSectorData({
            label:      secRRG.label,
            quadrant:   latestPt ? getQuadrant(latestPt.x, latestPt.y) : null,
            trail:      secRRG.trail ?? [],
            rsRatio:    latestPt?.x ?? null,   // weekly RRG — for trajectory display only
            rsMom:      latestPt?.y ?? null,
            aboveMA:    assetMatch?.above_ma ?? null,
            rsCurrent:  assetMatch?.rs_current ?? null,  // daily 100-day normalised ratio
            series:     assetMatch?.rs_series?.map(p => ({
              v:    p.rs / 100,
              ma:   p.ma != null ? p.ma / 100 : null,
              date: p.date,
            })) ?? null,
          });
        }
      }
      setSecLoad(false);

      // ── Price data ────────────────────────────────────────────────────────
      // matchType=null covers ETFs (GOLDBEES, SILVERBEES) and commodity cards
      // (YF:NG=F etc) that have no RS data but DO return from getIndexQuote.
      if (matchType === 'asset' || (matchType == null && idxPriceR.value?.ltp != null)) {
        const d = idxPriceR.value;
        if (d && !d._error && d.ltp != null) {
          // When the response is stale-cached (_stale=true) the history arrays
          // (daily_sessions, period_returns, sparklines) may be from a prior
          // successful response for a different symbol or an older fetch.
          // Zero them out — the live quote (ltp/ohlc) can still come from cache,
          // but frozen historical arrays must never be shown as current.
          const historyOk = !d._stale && d.has_history !== false;
          const _rawCloses = historyOk ? (d.sparkline_closes ?? []) : [];
          const _rawDates  = historyOk ? (d.sparkline_dates  ?? []) : [];
          const _spark = [..._rawCloses];
          const _sparkDates = [..._rawDates];
          if (d.ltp != null && (
            _spark.length === 0 ||
            Math.abs(_spark[_spark.length - 1] - d.ltp) > 0.01
          )) {
            _spark.push(d.ltp); // live intraday point — always rightmost
            _sparkDates.push(new Date().toISOString().split('T')[0]);
          }

          setPriceData({
            ltp:            d.ltp,
            change:         d.change,
            change_pct:     d.change_pct,
            open:           d.open,
            high:           d.high,
            low:            d.low,
            prev_close:     d.prev_close,
            high_52w:       historyOk ? (d.high_52w ?? null) : null,
            low_52w:        historyOk ? (d.low_52w  ?? null) : null,
            period_returns: historyOk ? (d.period_returns ?? []) : [],
            daily_sessions: historyOk ? (d.daily_sessions ?? []) : [],
            has_history:    historyOk && d.has_history !== false,
            sparkCloses:    _spark.length >= 2 ? _spark : [],
            sparkDates:     _sparkDates.length === _spark.length ? _sparkDates : [],
          });
        } else {
          setPriceErr(d?._error ?? (idxPriceR.reason?.message ?? 'Price unavailable'));
        }
        setPriceLoad(false);

      } else if (matchType === 'stock') {
        // Use the speculatively-fired promises from above (already in-flight).
        // For stocks these complete in parallel with Phase 1 — no extra wait.
        Promise.allSettled([_stockQuoteP, _stockHistoryP]).then(([qR, hR]) => {
          const q = qR.status === 'fulfilled' ? qR.value : null;
          const h = hR.status === 'fulfilled' ? hR.value : null;

          if (!q) {
            setPriceErr(qR.reason?.message ?? 'Price unavailable');
            setPriceLoad(false);
            return;
          }

          // Period returns + 52W + strip + sparkline all from eod_prices via /history.
          // candles (Kite) kept in h for any chart consumer but not used here.
          const historyOk        = h != null && !h._stale && h.has_history !== false;
          const period_returns   = h?.period_returns   ?? [];
          const high_52w_hist    = h?.high_52w         ?? null;
          const low_52w_hist     = h?.low_52w          ?? null;
          const last_bar         = h?.last_bar         ?? null;
          const stale_suppressed = h?.stale_suppressed ?? false;
          const has_history      = h?.has_history      ?? false;
          const daily_sessions   = historyOk ? (h.daily_sessions   ?? []) : [];
          const sparkCloses      = historyOk ? (h.sparkline_closes ?? []) : [];
          const sparkDates       = historyOk ? (h.sparkline_dates  ?? []) : [];

          // Normalise field names (stock endpoint: chg_pct; index: change_pct)
          const chgPct = q.change_pct ?? q.chg_pct ?? null;
          const chgAbs = q.change ?? (q.ltp != null && q.prev_close != null
            ? q.ltp - q.prev_close : null);

          setPriceData({
            ltp:            q.ltp,
            change:         chgAbs != null ? +chgAbs.toFixed(2) : null,
            change_pct:     chgPct,
            open:           q.open,
            high:           q.high,
            low:            q.low,
            prev_close:     q.prev_close,
            high_52w:       high_52w_hist,
            low_52w:        low_52w_hist,
            company_name:   q.company_name ?? null,
            period_returns,
            daily_sessions,
            sparkCloses,
            sparkDates:     sparkDates.length === sparkCloses.length ? sparkDates : [],
            last_bar,
            stale_suppressed,
            has_history,
          });
        }).catch(e => {
          setPriceErr(e.message);
        }).finally(() => setPriceLoad(false));

      } else {
        // Unknown type — could be a new/renamed stock not yet in RS cache (e.g. TMCV after demerger).
        // Try the stock detail route first; fall back to index quote for true broad indices.
        Promise.allSettled([_stockQuoteP, _stockHistoryP]).then(([qR, hR]) => {
          const q = qR.status === 'fulfilled' ? qR.value : null;
          const h = hR.status === 'fulfilled' ? hR.value : null;

          if (q?.ltp != null) {
            // Stock data available — render exactly like matchType=stock
            const historyOk2       = h != null && !h._stale && h.has_history !== false;
            const period_returns   = h?.period_returns   ?? [];
            const high_52w_hist    = h?.high_52w         ?? null;
            const low_52w_hist     = h?.low_52w          ?? null;
            const last_bar         = h?.last_bar         ?? null;
            const stale_suppressed = h?.stale_suppressed ?? false;
            const has_history      = h?.has_history      ?? false;
            const daily_sessions   = historyOk2 ? (h.daily_sessions   ?? []) : [];
            const sparkCloses      = historyOk2 ? (h.sparkline_closes ?? []) : [];
            const sparkDates       = historyOk2 ? (h.sparkline_dates  ?? []) : [];
            const chgPct = q.change_pct ?? q.chg_pct ?? null;
            const chgAbs = q.change ?? (q.ltp != null && q.prev_close != null
              ? q.ltp - q.prev_close : null);
            setPriceData({
              ltp:            q.ltp,
              change:         chgAbs != null ? +chgAbs.toFixed(2) : null,
              change_pct:     chgPct,
              open:           q.open,
              high:           q.high,
              low:            q.low,
              prev_close:     q.prev_close,
              high_52w:       high_52w_hist,
              low_52w:        low_52w_hist,
              company_name:   q.company_name ?? null,
              period_returns,
              daily_sessions,
              sparkCloses,
              sparkDates:     sparkDates.length === sparkCloses.length ? sparkDates : [],
              last_bar,
              stale_suppressed,
              has_history,
            });
            setPriceLoad(false);
          } else {
            // Not a stock (or Kite not connected) — try index/asset quote (yfinance)
            const d = idxPriceR.value;
            if (d && !d._error && d.ltp != null) {
              const _rawCloses = d.sparkline_closes ?? [];
              const _rawDates  = d.sparkline_dates  ?? [];
              const _spark = [..._rawCloses];
              const _sparkDates = [..._rawDates];
              if (d.ltp != null && (
                _spark.length === 0 ||
                Math.abs(_spark[_spark.length - 1] - d.ltp) > 0.01
              )) {
                _spark.push(d.ltp);
                _sparkDates.push(new Date().toISOString().split('T')[0]);
              }
              setPriceData({
                ltp:            d.ltp,
                change:         d.change,
                change_pct:     d.change_pct,
                high_52w:       d.high_52w ?? null,
                low_52w:        d.low_52w  ?? null,
                period_returns: d.period_returns ?? [],
                daily_sessions: d.daily_sessions ?? [],
                sparkCloses:    _spark.length >= 2 ? _spark : [],
                sparkDates:     _sparkDates.length === _spark.length ? _sparkDates : [],
              });
            } else {
              setPriceErr(d?._error ?? 'Price unavailable');
            }
            setPriceLoad(false);
          }
        });
      }
    });
  }, [ticker?.name, open, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch new-section data from /api/sidepanel/{ticker} ───────────────────
  React.useEffect(() => {
    if (!ticker?.name || !open) { setPanelMeta(null); return; }
    const BASE = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
    // Capture at effect-entry: true only when triggered by handleRefresh (refreshing=true).
    // isRefreshRef.current is reset to false inside the main effect before any async .catch fires,
    // so we can't read it there — capture refreshing state here instead (set in same batch as refreshKey).
    const isManualRefresh = refreshing;
    let cancelled = false;
    fetch(`${BASE}/api/sidepanel/${encodeURIComponent(ticker.name)}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        if (cancelled) return;
        if (d) setPanelMeta(d);
        setLastRefreshed(new Date());
        setRefreshErr(false);
        setRefreshing(false);
      })
      .catch(() => {
        if (cancelled) return;
        setRefreshing(false);
        if (isManualRefresh) {
          setRefreshErr(true);
          clearTimeout(refreshErrTimerRef.current);
          refreshErrTimerRef.current = setTimeout(() => setRefreshErr(false), 2500);
        }
      });
    return () => { cancelled = true; };
  }, [ticker?.name, open, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset refresh state when the ticker itself changes
  React.useEffect(() => {
    setLastRefreshed(null);
    setRefreshErr(false);
    clearTimeout(refreshErrTimerRef.current);
    isRefreshRef.current = false;
    setRefreshing(false);
  }, [ticker?.name]);

  // ── Derived display values ─────────────────────────────────────────────────
  const name         = ticker?.name ?? '';
  const _isBenchmark = isBenchmark(name);
  const isF0         = matchInfo?.isF0 === true;
  const isStock      = matchInfo?.type === 'stock';
  const isAsset      = matchInfo?.type === 'asset';
  const isSector     = isAsset && sectorData != null; // asset that has an RRG trail
  const showSector   = isSector || isStock;            // either path uses SectorRotationDisplay

  // 52W data comes from priceData (populated from /history eod_prices for stocks,
  // from the index endpoint for assets). No separate w52 fetch.
  const high52w = priceData?.high_52w ?? null;
  const low52w  = priceData?.low_52w  ?? null;

  // Subtitle: [full label · ] category [ · F&O]
  // Priority: RS match label > central registry label > null
  const rsLabel  = matchInfo?.label && matchInfo.label.toLowerCase() !== name.toLowerCase()
    ? matchInfo.label : null;
  // For NSE stocks: company_name comes from kite_instruments via the stock quote response.
  // Falls back through resolveLabel (covers indices/sectors in the registry).
  const fullLabel = rsLabel ?? priceData?.company_name ?? resolveLabel(name);
  const subtitleParts = [fullLabel, matchInfo?.sectorLabel, isF0 ? 'F&O' : null].filter(Boolean);
  const subtitle = subtitleParts.join(' · ') || ticker?.sector || '';

  function handleRefresh() {
    if (refreshing || !ticker?.name || !open) return;
    isRefreshRef.current = true;
    setRefreshing(true);
    setRefreshKey(k => k + 1);
  }

  const refreshedLabel = lastRefreshed
    ? lastRefreshed.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:false, timeZone:'Asia/Kolkata' })
    : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes sp-pulse {
          0%,100%{ opacity:1 } 50%{ opacity:.45 }
        }
        @keyframes sp-shimmer {
          0%   { background-position: 200% center }
          100% { background-position:-200% center }
        }
      `}</style>

      <div className={`side-overlay${open ? ' open' : ''}`} onClick={close}/>

      <div ref={panelRef} className={`side-panel${open ? ' open' : ''}`}>
        {open && ticker && (
          <div style={{ padding: 16 }}>

            {/* ── HEADER — sticky so ticker stays visible while scrolling ──── */}
            <div style={{
              position:'sticky', top:0, zIndex:10,
              background:'var(--surface)',
              margin:'-16px -16px 10px',
              padding:'12px 16px 10px',
              borderBottom:'.5px solid var(--hair)',
            }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ flex:1, minWidth:0, paddingRight:8 }}>
                  {/* Ticker + F&O badge + switcher */}
                  {tickerSearch ? (() => {
                    // Inline fuzzy scorer for ticker switcher
                    const _fz = (h, n) => {
                      if (!h || !n) return 0;
                      if (h === n) return 3;
                      if (h.startsWith(n)) return 2;
                      if (h.includes(n)) return 1;
                      let hi = 0;
                      for (let ni = 0; ni < n.length; ni++) {
                        const pos = h.indexOf(n[ni], hi);
                        if (pos === -1) return 0;
                        hi = pos + 1;
                      }
                      return 0.5;
                    };
                    const q = tickerQ.toUpperCase();
                    const tickerResults = q.length > 0
                      ? searchUniverse
                          .map(item => ({ item, score: Math.max(_fz(item.sym, q), _fz((item.name || '').toUpperCase(), q)) }))
                          .filter(x => x.score > 0)
                          .sort((a, b) => b.score - a.score)
                          .slice(0, 8)
                          .map(x => x.item)
                      : [];
                    return (
                      <div style={{ position:'relative' }}>
                        <input
                          ref={tickerInputRef}
                          value={tickerQ}
                          onChange={e => setTickerQ(e.target.value.toUpperCase())}
                          onKeyDown={e => {
                            if (e.key === 'Escape') { setTickerSearch(false); setTickerQ(''); }
                            if (e.key === 'Enter' && tickerResults[0]) {
                              addToHistory(tickerResults[0].sym);
                              openPanel({ name: tickerResults[0].sym });
                              setTickerSearch(false); setTickerQ('');
                            }
                          }}
                          onBlur={() => setTimeout(() => { setTickerSearch(false); setTickerQ(''); }, 200)}
                          placeholder="Switch ticker…"
                          autoComplete="off"
                          style={{
                            background:'var(--surface-2)', border:'0.5px solid var(--hair)',
                            borderRadius:'var(--r-2)', color:'var(--ink)', fontSize:12,
                            padding:'3px 10px', width:180, outline:'none',
                            fontFamily:'var(--font-ui)',
                          }}
                        />
                        {tickerResults.length > 0 && (
                          <div style={{
                            position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:9999,
                            background:'var(--surface)', border:'0.5px solid var(--hair)',
                            borderRadius:'var(--r-3)', boxShadow:'var(--shadow-panel)',
                            minWidth:220, maxHeight:280, overflowY:'auto',
                          }}>
                            {tickerResults.map(({sym, name: n}) => (
                              <div key={sym}
                                onMouseDown={e => { e.preventDefault(); addToHistory(sym); openPanel({ name: sym }); setTickerSearch(false); setTickerQ(''); }}
                                style={{
                                  padding:'5px 12px', cursor:'pointer', fontSize:11, fontWeight:600,
                                  color:'var(--ink)', fontFamily:'var(--font-ui)', letterSpacing:'.02em',
                                  display:'flex', alignItems:'center', gap:6,
                                }}
                                onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
                                onMouseLeave={e => e.currentTarget.style.background='transparent'}
                              >
                                {sym}
                                {n && <span style={{fontSize:10, color:'var(--ink-3)', fontWeight:400, marginLeft:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:120}}>{n}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })() : (
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:17, color:'var(--ink)', letterSpacing:'.01em' }}>
                      {name}
                    </span>
                    {isF0 && (
                      <span style={{
                        fontSize:11, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase',
                        padding:'1px 5px', borderRadius:3,
                        background:'rgba(26,74,138,.12)', color:'#1a4a8a',
                        border:'.5px solid rgba(26,74,138,.25)',
                      }}>F&amp;O</span>
                    )}
                    <button
                      onClick={() => { setTickerSearch(true); setTickerQ(''); setTimeout(() => tickerInputRef.current?.focus(), 40); }}
                      title="Switch ticker"
                      style={{ background:'transparent', border:'none', cursor:'pointer', padding:'2px 4px', color:'var(--ink-3)', display:'flex', alignItems:'center' }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    </button>
                  </div>
                  )}
                  {/* Full company name — shown when it differs from the ticker */}
                  {fullLabel && (
                    <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {fullLabel}
                    </div>
                  )}
                  {/* Sector / category line */}
                  {(matchInfo?.sectorLabel || ticker?.sector) && (
                    <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:2, opacity:.85 }}>
                      {matchInfo?.sectorLabel || ticker?.sector}
                      {isF0 && ' · F&O'}
                    </div>
                  )}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                  {refreshErr ? (
                    <span style={{ fontSize:9, color:'var(--amber)', letterSpacing:'.03em', userSelect:'none' }}>
                      couldn&apos;t refresh
                    </span>
                  ) : refreshedLabel ? (
                    <span style={{ fontSize:9, color:'var(--ink-3)', letterSpacing:'.03em', userSelect:'none' }}>
                      {refreshedLabel}
                    </span>
                  ) : null}
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    title={refreshedLabel ? `Last refreshed ${refreshedLabel} — click to refresh` : 'Refresh panel data'}
                    style={{
                      background:'var(--surface-2)',
                      border: refreshErr ? '.5px solid var(--amber)' : '.5px solid var(--hair-strong)',
                      borderRadius:4, padding:'3px 7px', cursor: refreshing ? 'default' : 'pointer',
                      opacity: refreshing ? 0.6 : 1,
                      display:'inline-flex', alignItems:'center', justifyContent:'center',
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"
                      stroke="var(--ink-3)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                      style={{ animation: refreshing ? 'ptr-spin .8s linear infinite' : 'none' }}
                    >
                      <path d="M21 12a9 9 0 1 1-2.64-6.36"/>
                      <path d="M21 4v4.5h-4.5"/>
                    </svg>
                  </button>
                  <button onClick={close} style={{
                    background:'var(--surface-2)', border:'.5px solid var(--hair-strong)',
                    borderRadius:4, padding:'3px 8px', cursor:'pointer', fontSize:13, color:'var(--ink-2)',
                  }}>✕</button>
                </div>
              </div>
            </div>

            {/* ── LTP + ABSOLUTE + % CHANGE ────────────────────────────────── */}
            <div style={{ marginBottom:10 }}>
              {priceLoad ? (
                <><Skeleton w="45%" h={26} mb={4}/><Skeleton w="50%" h={13}/></>
              ) : priceErr ? (
                <div style={{ fontSize:11, color:'var(--down-focus)', padding:'6px 0' }}>
                  {priceErr.includes('503') || priceErr.includes('Kite')
                    ? 'Kite not connected — log in to see live price'
                    : `Price unavailable: ${priceErr}`}
                </div>
              ) : priceData?.ltp != null ? (
                <>
                  {/* LTP row — price on left, sparkline stretches to fill the rest */}
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ fontFamily:'var(--font-data)', fontWeight:700, fontSize:22, color:'var(--ink)', flexShrink:0 }}>
                      ₹{fmt(priceData.ltp)}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <MiniSparkline closes={priceData.sparkCloses}
                        dates={priceData.sparkDates}
                        crosshairDate={crosshairDate}
                        onCrosshairChange={handleCrosshair}/>
                    </div>
                  </div>
                  {/* Absolute change + % on one line */}
                  {priceData.change_pct != null && (
                    <div style={{
                      fontSize:11, marginTop:2, fontFamily:'var(--font-data)',
                      color: priceData.change_pct >= 0 ? 'var(--up-focus)' : 'var(--down-focus)',
                    }}>
                      {priceData.change != null && (
                        <>{priceData.change >= 0 ? '+' : ''}₹{fmt(priceData.change)}{' '}</>
                      )}
                      ({fmtP(priceData.change_pct)}) today
                    </div>
                  )}
                </>
              ) : null}
            </div>

            {/* ── OHLC ROW ─────────────────────────────────────────────────── */}
            {priceLoad ? (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:5, marginBottom:8 }}>
                {[0,1,2,3].map(i => <Skeleton key={i} w="100%" h={38} mb={0} br={4}/>)}
              </div>
            ) : priceData && (priceData.open != null || priceData.high != null) ? (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:5, marginBottom:8 }}>
                {[
                  { l:'Open', v: priceData.open              },
                  { l:'High', v: priceData.high              },
                  { l:'Low',  v: priceData.low               },
                  { l:'Prev', v: priceData.prev_close, muted: true },
                ].map(({ l, v, muted }) => (
                  <div key={l} style={{ background:'var(--surface-2)', borderRadius:4, padding:'4px 6px' }}>
                    <div style={{ fontSize:11, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:2 }}>{l}</div>
                    <div style={{
                      fontFamily:'var(--font-data)', fontSize:11, fontWeight:600,
                      color: muted ? 'var(--ink-3)' : 'var(--ink)',
                    }}>₹{fmt(v)}</div>
                  </div>
                ))}
              </div>
            ) : null}

            {/* ── 52W H/L SLIDER ──────────────────────────────────────────── */}
            {priceLoad ? null : (
              <RangeBar52W
                ltp={priceData?.ltp}
                high52w={high52w}
                low52w={low52w}
                loading={false}
              />
            )}

            <Divider/>

            {/* ── PERIOD PERFORMANCE ──────────────────────────────────────── */}
            <SL>Period Performance</SL>
            <div style={{ marginBottom:14 }}>
              {priceData?.has_history === false ? (
                <div style={{ fontSize:11, color:'var(--ink-3)', padding:'6px 0' }}>
                  no historical data
                </div>
              ) : (
                <>
                  <PeriodWaterfall
                    periods={priceData?.period_returns ?? []}
                    loading={priceLoad}
                  />
                  {priceData?.last_bar && (
                    <div style={{ fontSize:10, color:'var(--ink-3)', marginTop:2, textAlign:'right' }}>
                      data as of {priceData.last_bar}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── DAILY LAST 15 SESSIONS ──────────────────────────────────── */}
            {priceLoad ? (
              <><SL>Daily — Last 15 Sessions (Recent → Past)</SL><SkeletonBlock h={80} mb={12}/></>
            ) : priceData?.daily_sessions?.length > 0 ? (
              <>
                <SL>Daily — Last 15 Sessions (Recent → Past)</SL>
                <div className="sp-daily-grid" style={{ marginBottom:12 }}>
                  {priceData.daily_sessions.map((d, i) => {
                    const s = hc(d.v);
                    return (
                      <div key={i} className="sp-day-chip" style={{ background:s.bg, color:s.color }}>
                        <div style={{ fontSize:10, opacity:.8, marginBottom:1 }}>{d.label}</div>
                        <div style={{ fontWeight:700, fontSize:10 }}>{fmtP(d.v)}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null}

            <Divider/>

            {/* ── VS NIFTY 50 ─────────────────────────────────────────────── */}
            {!_isBenchmark && (
              <>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                  <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'var(--ink-3)' }}>
                    Relative Strength vs NIFTY 50 · 20W
                  </div>
                  {rsData && rsData.signal != null && (
                    <span title="Strength = 20W daily RS ratio vs NIFTY · Momentum = ±0.5% band around 20-day MA">
                      <CompoundPill
                        strength={rsData.ratio == null ? 'Equal' : rsData.ratio > 1.005 ? 'Strong' : rsData.ratio < 0.995 ? 'Weak' : 'Equal'}
                        momentum={rsData.signal}
                      />
                    </span>
                  )}
                </div>
                {rsLoad ? (
                  <div style={{ marginBottom:14 }}>
                    <SkeletonBlock h={80} mb={6}/>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:5 }}>
                      {[0,1,2,3].map(i => <Skeleton key={i} w="100%" h={38} mb={0} br={4}/>)}
                    </div>
                  </div>
                ) : rsData?.building ? (
                  <div style={{ fontSize:11, marginBottom:14, display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{
                      fontSize:9, fontWeight:600, padding:'1px 4px', borderRadius:3,
                      background:'rgba(200,146,42,.15)', color:'var(--amber-ink)', letterSpacing:'.04em',
                    }}>BUILDING</span>
                    <span style={{ color:'var(--ink-3)', fontStyle:'italic' }}>
                      RS accumulating — fewer than 20 trading days of history
                    </span>
                  </div>
                ) : rsData?.series?.length >= 4 ? (
                  <div className="card" style={{ padding:'8px 10px', marginBottom:14 }}>
                    <RSMiniChart series={rsData.series}
                      crosshairDate={crosshairDate}
                      onCrosshairChange={handleCrosshair}/>
                    <div style={{ fontSize:10, color:'var(--ink-3)', opacity:.6, marginTop:5 }}>
                      Green = above 20-day MA · Red = below · Black = MA
                    </div>
                    {/* 4-tile grid: RS 1W / RS 1M / RS 3M / Ratio */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:5, marginTop:8 }}>
                      {[
                        { l:'RS 1W', v:fmtRS(rsData.rs1W), c:rsData.rs1W==null?'var(--ink-3)':rsData.rs1W>=0?'var(--up-focus)':'var(--down-focus)' },
                        { l:'RS 1M', v:fmtRS(rsData.rs1M), c:rsData.rs1M==null?'var(--ink-3)':rsData.rs1M>=0?'var(--up-focus)':'var(--down-focus)' },
                        { l:'RS 3M', v:fmtRS(rsData.rs3M), c:rsData.rs3M==null?'var(--ink-3)':rsData.rs3M>=0?'var(--up-focus)':'var(--down-focus)' },
                        { l:'Ratio', v:rsData.ratio!=null?rsData.ratio.toFixed(3):'—', c:'var(--ink)' },
                      ].map(item => (
                        <div key={item.l} style={{ background:'var(--surface-2)', borderRadius:4, padding:'5px 7px' }}>
                          <div style={{ fontSize:11, color:'var(--ink-3)', marginBottom:2, textTransform:'uppercase', letterSpacing:'.04em' }}>{item.l}</div>
                          <div style={{ fontSize:11, fontWeight:700, color:item.c, fontFamily:'var(--font-data)' }}>{item.v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : !rsLoad ? (
                  <div style={{ fontSize:11, color:'var(--ink-3)', fontStyle:'italic', marginBottom:14 }}>
                    No RS data available
                  </div>
                ) : null}
              </>
            )}

            {/* ── SECTOR / RRG — unified for both sector indices and F&O stocks */}
            {showSector && (
              <>
                <Divider/>
                <SL>{isStock ? 'Sector Performance' : 'Sector Rotation (RRG)'}</SL>
                <SectorRotationDisplay
                  sectorData={sectorData}
                  secLoad={secLoad}
                  crosshairDate={crosshairDate}
                  onCrosshairChange={handleCrosshair}
                  isStock={isStock}
                />
              </>
            )}

            {/* ── WATCHLISTS ───────────────────────────────────────────────── */}
            {(panelMeta?.watchlists?.length > 0 || panelMeta?.ticker_note) && (
              <>
                <Divider/>
                <SL>Watchlists</SL>
                {panelMeta?.ticker_note && (
                  <div style={{ fontSize:11, color:'var(--ink-2)', fontStyle:'italic',
                    marginBottom:6, lineHeight:1.4 }}>
                    {panelMeta.ticker_note}
                  </div>
                )}
                {panelMeta?.watchlists?.length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {panelMeta.watchlists.map(wl => (
                      <span key={wl.id} style={{
                        fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:12, cursor:'default',
                        background: wl.is_strategy_owned ? 'rgba(26,74,138,.10)' : 'var(--surface-2)',
                        color:      wl.is_strategy_owned ? '#1a4a8a'             : 'var(--ink-2)',
                        border:     wl.is_strategy_owned ? '.5px solid rgba(26,74,138,.25)' : '.5px solid var(--hair-strong)',
                      }}>
                        {wl.name}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── OPEN POSITIONS ───────────────────────────────────────────── */}
            {panelMeta?.open_positions?.length > 0 && (
              <>
                <Divider/>
                <SL>Open Positions</SL>
                {panelMeta.open_positions.map((pos, i) => (
                  <div key={i} style={{
                    display:'grid', gridTemplateColumns:'1fr auto', gap:6,
                    padding:'6px 0', borderBottom:'.5px solid var(--hair)',
                  }}>
                    <div>
                      <div style={{ fontSize:11, fontWeight:600, color:'var(--ink)' }}>
                        {pos.direction ?? ''} {pos.lots != null && pos.lot_size != null ? `${pos.lots} lot${pos.lots !== 1 ? 's' : ''} × ${pos.lot_size}` : ''}
                      </div>
                      {pos.strategy && (
                        <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:2 }}>{pos.strategy}</div>
                      )}
                    </div>
                    <div style={{ textAlign:'right' }}>
                      {pos.entry_price != null && (
                        <div style={{ fontSize:11, fontFamily:'var(--font-data)', fontWeight:600, color:'var(--ink)' }}>
                          ₹{fmt(pos.entry_price)}
                        </div>
                      )}
                      {pos.entry_date && (
                        <div style={{ fontSize:11, color:'var(--ink-3)' }}>{pos.entry_date}</div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* ── STRATEGY TRACKING ────────────────────────────────────────── */}
            {panelMeta?.strategy_tracking?.length > 0 && (
              <>
                <Divider/>
                <SL>Strategy Signals</SL>
                {panelMeta.strategy_tracking.map((t, i) => (
                  <div key={t.strategy_id + i} style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    padding: '5px 0',
                    borderBottom: i < panelMeta.strategy_tracking.length - 1 ? '.5px solid var(--hair)' : 'none',
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)' }}>{t.strategy_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 1 }}>
                        {t.role === 'sip_pick' ? 'SIP pick' : t.direction === 'long' ? '▲ Bullish signal' : '▼ Bearish signal'}
                        {t.last_signal_date && ` · ${t.last_signal_date}`}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 3, flexShrink: 0, marginLeft: 6,
                      background: t.direction === 'long' ? 'rgba(26,106,26,.12)' : 'rgba(136,30,16,.12)',
                      color: t.direction === 'long' ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
                      border: t.direction === 'long' ? '.5px solid rgba(26,106,26,.28)' : '.5px solid rgba(136,30,16,.28)',
                      textTransform: 'uppercase', letterSpacing: '.04em',
                    }}>
                      {t.direction === 'long' ? 'Long' : 'Short'}
                    </span>
                  </div>
                ))}
              </>
            )}

            {/* ── RELATED NEWS ─────────────────────────────────────────────── */}
            {panelMeta?.related_news?.length > 0 && (
              <>
                <Divider/>
                <SL>Related News</SL>
                {panelMeta.related_news.map((item, i) => {
                  const diff = Date.now() - new Date(item.published).getTime();
                  const m = Math.floor(diff / 60000);
                  const ago = m < 60 ? `${m}m ago` : m < 1440 ? `${Math.floor(m/60)}h ago` : `${Math.floor(m/1440)}d ago`;
                  const isHigh = item.impact === 'HIGH';
                  return (
                    <div key={item.item_id || i} style={{
                      padding:'7px 0',
                      borderBottom: i < panelMeta.related_news.length - 1 ? '.5px solid var(--hair)' : 'none',
                    }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:5, marginBottom:3 }}>
                        {isHigh && (
                          <span style={{ fontSize:9, fontWeight:700, padding:'1px 4px', borderRadius:2,
                            background:'#fde8e8', color:'#b71c1c', flexShrink:0, marginTop:2 }}>HIGH</span>
                        )}
                        <a href={item.url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize:11, fontWeight:600, color:'var(--ink)', lineHeight:1.45,
                            textDecoration:'none', flex:1 }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                          {item.title}
                        </a>
                      </div>
                      <div style={{ fontSize:10, color:'var(--ink-3)' }}>
                        {item.source} · {ago}
                      </div>
                      {item.rationale && (
                        <div style={{ fontSize:10, color:'var(--ink-2)', lineHeight:1.45, marginTop:3,
                          paddingLeft:6, borderLeft:'2px solid var(--hair-strong)' }}>
                          {item.rationale}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

          </div>
        )}
      </div>
    </>
  );
}
